import { useEffect, useState } from 'react';
import { useT } from '../../lib/i18n';
import { countdown, formatDateLong, formatDuration, formatTime, useMoney } from '../../lib/format';
import { ScreenHeading } from '../shell/PortalShell';
import type { DepositGateway, DepositGatewayCode, Hold, PortalBranch, Service, Staff } from '../../lib/types';

/**
 * Final review.
 *
 * The hold countdown is shown honestly rather than hidden: the customer is
 * holding a real slot that someone else cannot take, and they deserve to know
 * how long they have. When it lapses the confirm button disables itself instead
 * of failing on submit.
 */
export function ReviewStep({
  branch,
  services,
  staff,
  hold,
  notes,
  depositRequired,
  gateways,
  gateway,
  onGatewayChange,
  onNotesChange,
  onConfirm,
  onHoldExpired,
  busy,
}: {
  branch: PortalBranch | null;
  services: Service[];
  staff: Staff | null;
  hold: Hold | null;
  notes: string;
  depositRequired: number;
  /** The gateways the deposit can be paid on; a picker shows when there are several. */
  gateways: DepositGateway[];
  gateway: DepositGatewayCode | null;
  onGatewayChange: (gateway: DepositGatewayCode) => void;
  onNotesChange: (value: string) => void;
  onConfirm: () => void;
  onHoldExpired: () => void;
  busy: boolean;
}): JSX.Element {
  const { t, lang, pick } = useT();
  const money = useMoney();
  const [remaining, setRemaining] = useState<string | null>(hold ? countdown(hold.expires_at) : null);

  useEffect(() => {
    if (!hold) return;
    const tick = (): void => {
      const value = countdown(hold.expires_at);
      setRemaining(value);
      if (value === null) onHoldExpired();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [hold, onHoldExpired]);

  const timezone = branch?.timezone ?? 'UTC';
  const total = services.reduce((sum, s) => sum + s.price, 0);
  const duration = services.reduce((sum, s) => sum + s.duration_minutes, 0);
  const expired = hold != null && remaining === null;

  return (
    <div className="bk-rise">
      <ScreenHeading title={t('review_title')} />

      {hold && (
        <div
          className="mb-4 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          role="status"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span className="font-medium">
            {expired ? t('hold_expired') : `${t('holding_slot')} · ${t('hold_expires_in')} ${remaining}`}
          </span>
        </div>
      )}

      <div className="bk-card mb-4 divide-y divide-zinc-100">
        {hold && (
          <Row
            icon={
              <path
                d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            }
            title={formatDateLong(hold.start_at, timezone, lang)}
            detail={`${formatTime(hold.start_at, timezone, lang)} – ${formatTime(hold.end_at, timezone, lang)}`}
          />
        )}
        {branch && (
          <Row
            icon={
              <>
                <path
                  d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1118 0z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
              </>
            }
            title={lang === 'ar' && branch.name_ar ? branch.name_ar : branch.name}
            detail={(lang === 'ar' && branch.address_ar ? branch.address_ar : branch.address) ?? ''}
          />
        )}
        <Row
          icon={
            <path
              d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          }
          title={staff ? pick(staff) : t('any_staff')}
          detail=""
        />
      </div>

      <div className="bk-card mb-4 p-4">
        <ul className="m-0 list-none space-y-2.5 p-0">
          {services.map((service) => (
            <li key={service.item_id} className="flex items-baseline gap-3 text-sm">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-zinc-900">{pick(service)}</span>
                <span className="text-xs text-zinc-400">
                  {formatDuration(service.duration_minutes, lang)}
                </span>
              </span>
              <span className="font-medium text-zinc-900">
                {money(service.price)}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-3 border-t border-zinc-100 pt-3 text-sm">
          <div className="flex justify-between text-zinc-500">
            <span>{t('duration')}</span>
            <span>{formatDuration(duration, lang)}</span>
          </div>
          <div className="mt-1 flex justify-between text-base font-semibold text-zinc-900">
            <span>{t('total')}</span>
            <span>{money(total)}</span>
          </div>
          {depositRequired > 0 && (
            <div
              className="mt-3 rounded-xl px-3 py-2.5 text-sm"
              style={{ background: 'var(--accent-soft)' }}
            >
              <div className="flex justify-between font-semibold" style={{ color: 'var(--accent)' }}>
                <span>{t('deposit_due')}</span>
                <span>{money(depositRequired)}</span>
              </div>
              <p className="m-0 mt-0.5 text-xs text-zinc-500">{t('pay_rest_at_salon')}</p>
              {gateways.length > 1 && (
                <div className="mt-3">
                  <div className="mb-1.5 text-xs font-semibold text-zinc-600">{t('choose_gateway')}</div>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('choose_gateway')}>
                    {gateways.map((g) => {
                      const selected = g.gateway === gateway;
                      return (
                        <button
                          key={g.gateway}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => onGatewayChange(g.gateway)}
                          className="rounded-full border px-3 py-1.5 text-sm font-medium transition"
                          style={{
                            borderColor: selected ? 'var(--accent)' : 'rgb(228 228 231)',
                            background: selected ? 'var(--accent)' : 'white',
                            color: selected ? 'white' : 'rgb(63 63 70)',
                          }}
                          title={g.requires_phone ? t('gateway_needs_phone') : undefined}
                        >
                          {lang === 'ar' ? g.name_ar : g.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <label className="mb-5 block">
        <span className="bk-label">{t('notes_label')}</span>
        <textarea
          className="bk-input py-3"
          rows={3}
          style={{ minHeight: 88, resize: 'vertical' }}
          placeholder={t('notes_placeholder')}
          value={notes}
          maxLength={1000}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </label>

      <button
        type="button"
        className="bk-btn bk-btn-primary w-full"
        disabled={busy || expired || !hold}
        onClick={onConfirm}
      >
        {busy ? t('loading') : depositRequired > 0 ? t('pay_and_confirm') : t('confirm_booking')}
      </button>
    </div>
  );
}

function Row({
  icon,
  title,
  detail,
}: {
  icon: JSX.Element;
  title: string;
  detail: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 p-4">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0 text-zinc-400" aria-hidden>
        {icon}
      </svg>
      <div className="min-w-0">
        <p className="m-0 text-sm font-medium text-zinc-900">{title}</p>
        {detail && <p className="m-0 mt-0.5 text-xs text-zinc-500">{detail}</p>}
      </div>
    </div>
  );
}
